import { useMemo, useState, useEffect } from "react";
import { geoEqualEarth, geoAlbersUsa, geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import worldData from "@/data/countries-110m.json";
import { colorHex, inkColor } from "@/lib/constants";
import { Globe, MapPin, Layers, ArrowLeft, Search, Star, Loader2, Stamp } from "lucide-react";

const GEO = feature(worldData, worldData.objects.countries);

const ALIAS = {
  "United States": "United States of America", USA: "United States of America", US: "United States of America",
  UK: "United Kingdom", England: "United Kingdom", Britain: "United Kingdom",
  "Czech Republic": "Czechia", Russia: "Russia", "Russian Federation": "Russia",
};
const norm = (n) => ALIAS[n] || n;

// Drillable countries → lazy-loaded region geometry + projection + label.
// Region files are code-split (dynamic import) so they never bloat the main bundle.
const DRILLS = {
  "United States of America": {
    label: "United States", projection: geoAlbersUsa, regionWord: "state",
    load: async () => { const m = await import("@/data/us-states-10m.json"); const d = m.default || m; return feature(d, d.objects.states).features; },
  },
  Canada: {
    label: "Canada", projection: geoMercator, regionWord: "province",
    load: async () => { const m = await import("@/data/canada-provinces.json"); return (m.default || m).features; },
  },
  Australia: {
    label: "Australia", projection: geoMercator, regionWord: "state / territory",
    load: async () => { const m = await import("@/data/australia-states.json"); return (m.default || m).features; },
  },
  "United Kingdom": {
    label: "United Kingdom", projection: geoMercator, regionWord: "county",
    load: async () => { const m = await import("@/data/uk-regions.json"); return (m.default || m).features; },
  },
  Germany: {
    label: "Germany", projection: geoMercator, regionWord: "state",
    load: async () => { const m = await import("@/data/germany-states.json"); return (m.default || m).features; },
  },
};

// Opacity ramp so a country's national-flag ink reads deeper the more pieces you own.
function inkOpacity(count, max) {
  if (!count) return 1;
  const t = max > 1 ? Math.log(count + 1) / Math.log(max + 1) : 1;
  return 0.55 + 0.45 * t;
}

const W = 800, H = 380;

export function CollectionWorldMap({ data }) {
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);
  const [drill, setDrill] = useState(null); // null (world) | canonical country name
  const [drillFeatures, setDrillFeatures] = useState(null); // loaded region geometry
  const [drillLoading, setDrillLoading] = useState(false);

  const counts = useMemo(() => {
    const m = {};
    (data?.countries || []).forEach((c) => { m[norm(c.country)] = c.count; });
    return m;
  }, [data]);
  const max = useMemo(() => Math.max(1, ...Object.values(counts)), [counts]);
  const worldPath = useMemo(() => geoPath(geoEqualEarth().fitSize([W, H], GEO)), []);

  const drilldowns = data?.drilldowns || {};
  const drillableCountries = Object.keys(DRILLS).filter((c) => (drilldowns[c] || []).length > 0);

  const rows = data?.countries || [];
  const selectedRow = rows.find((r) => norm(r.country) === (selected && norm(selected)));

  // Active drill data
  const drillCfg = drill ? DRILLS[drill] : null;
  const drillRows = useMemo(() => (drill ? (drilldowns[drill] || []) : []), [drill, drilldowns]);
  const drillMap = useMemo(() => {
    const m = {};
    drillRows.forEach((r) => { m[r.region] = r; });
    return m;
  }, [drillRows]);
  const drillPath = useMemo(() => {
    if (!drillCfg || !drillFeatures) return null;
    const fc = { type: "FeatureCollection", features: drillFeatures };
    return geoPath(drillCfg.projection().fitSize([W, H], fc));
  }, [drillCfg, drillFeatures]);
  const selectedRegion = drillMap[selected] || null;

  // Lazy-load the region geometry when entering a drill-down.
  useEffect(() => {
    let cancelled = false;
    if (!drill) { setDrillFeatures(null); return; }
    setDrillLoading(true);
    setDrillFeatures(null);
    DRILLS[drill].load()
      .then((feats) => { if (!cancelled) setDrillFeatures(feats); })
      .catch(() => { if (!cancelled) setDrillFeatures([]); })
      .finally(() => { if (!cancelled) setDrillLoading(false); });
    return () => { cancelled = true; };
  }, [drill]);

  // Legend: distinct top colors present in current drill.
  const legendColors = useMemo(() => {
    const seen = [];
    drillRows.forEach((r) => { if (r.top_color && !seen.includes(r.top_color)) seen.push(r.top_color); });
    return seen;
  }, [drillRows]);

  if (!data || data.total_countries === 0) {
    return (
      <div className="text-center py-16 surface border rounded-2xl" data-testid="map-empty">
        <Globe className="w-10 h-10 mx-auto text-muted-page mb-3" />
        <p className="font-display text-2xl">No countries mapped yet</p>
        <p className="text-muted-page mt-1">Add pieces with a country of origin to light up the map.</p>
      </div>
    );
  }

  const enterDrill = (country) => { setSelected(null); setHover(null); setDrill(country); };
  const goWorld = () => { setSelected(null); setHover(null); setDrill(null); };
  const topRegion = data?.top_region;

  return (
    <div className="space-y-6" data-testid="collection-world-map">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <div className="surface border rounded-2xl p-5 text-center card-lift" data-testid="map-stat-pieces">
          <div className="flex items-center justify-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><Layers className="w-4 h-4" /> Total pieces</div>
          <p className="font-display text-3xl font-bold mt-1">{data.total_pieces}</p>
        </div>
        <div className="surface border rounded-2xl p-5 text-center card-lift" data-testid="map-stat-countries">
          <div className="flex items-center justify-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><Globe className="w-4 h-4" /> Countries</div>
          <p className="font-display text-3xl font-bold mt-1">{data.total_countries}</p>
        </div>
        <div className="surface border rounded-2xl p-5 text-center card-lift" data-testid="map-stat-top">
          <div className="flex items-center justify-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><MapPin className="w-4 h-4" /> Top country</div>
          <p className="font-display text-xl font-bold mt-1 truncate">{data.top_country || "—"}</p>
        </div>
        <div className="surface border-2 rounded-2xl p-5 text-center" style={{ borderColor: "var(--honey-500)" }} data-testid="map-stat-top-region">
          <div className="flex items-center justify-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><Star className="w-4 h-4" /> Most collected state</div>
          {topRegion ? (
            <>
              <p className="font-display text-xl font-bold mt-1 brand-text truncate">{topRegion.region}</p>
              <p className="text-xs text-muted-page truncate">{topRegion.count} pc · {topRegion.country === "United States of America" ? "USA" : topRegion.country}</p>
            </>
          ) : (
            <p className="font-display text-xl font-bold mt-1 text-muted-page">—</p>
          )}
        </div>
      </div>

      <div className="surface border rounded-2xl p-4 relative">
        <div className="flex items-center justify-between gap-2 mb-2 min-h-[36px] flex-wrap">
          {drill ? (
            <button data-testid="map-back-world" onClick={goWorld} className="inline-flex items-center gap-1.5 text-sm font-semibold surface border rounded-full px-3 py-1.5 hover:shadow">
              <ArrowLeft className="w-4 h-4" /> Back to world
            </button>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              {drillableCountries.map((c) => (
                <button
                  key={c}
                  data-testid={`map-view-${DRILLS[c].label.replace(/[^a-zA-Z]/g, "-")}`}
                  onClick={() => enterDrill(c)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-3 py-1.5 text-white hover:shadow"
                  style={{ background: "var(--brand)" }}
                >
                  <Search className="w-4 h-4" /> {DRILLS[c].label}
                </button>
              ))}
            </div>
          )}
          <span className="font-tech text-xs uppercase tracking-widest text-muted-page">
            {drill ? `${drillCfg.label} · by ${drillCfg.regionWord}` : drillableCountries.length ? "Click a highlighted country to explore regions" : "World"}
          </span>
        </div>

        {!drill ? (
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="world-map-svg">
            {GEO.features.map((geo) => {
              const name = geo.properties.name;
              const count = counts[name] || 0;
              const canDrill = drillableCountries.includes(name);
              const isSel = selectedRow && norm(selectedRow.country) === name;
              return (
                <path
                  key={name}
                  d={worldPath(geo)}
                  fill={count ? inkColor(name) : "#ece7dd"}
                  fillOpacity={inkOpacity(count, max)}
                  stroke={canDrill ? "#b45309" : isSel ? "#3a2a10" : "#ffffff"}
                  strokeWidth={canDrill ? 1.2 : isSel ? 1.4 : 0.4}
                  style={{ cursor: count || canDrill ? "pointer" : "default", transition: "fill .2s, stroke-width .2s" }}
                  onMouseEnter={(e) => setHover({ name: canDrill ? `${name} — click to explore regions` : name, count, x: e.clientX, y: e.clientY })}
                  onMouseMove={(e) => setHover({ name: canDrill ? `${name} — click to explore regions` : name, count, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => { if (canDrill) { enterDrill(name); } else if (count) { setSelected(name); } }}
                />
              );
            })}
          </svg>
        ) : drillLoading || !drillFeatures || !drillPath ? (
          <div className="flex items-center justify-center gap-2 text-muted-page font-tech text-sm" style={{ height: H }} data-testid="region-map-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading {drillCfg.label} map…
          </div>
        ) : (
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="region-map-svg">
            {drillFeatures.map((geo, idx) => {
              const name = geo.properties.name;
              const r = drillMap[name];
              const fill = r ? colorHex(r.top_color) : "#ece7dd";
              const isSel = selected === name;
              const d = drillPath(geo);
              if (!d) return null;
              return (
                <path
                  key={`${name}-${idx}`}
                  data-testid={`region-${(name || "").replace(/[^a-zA-Z]/g, "-")}`}
                  d={d}
                  fill={fill}
                  stroke={isSel ? "#3a2a10" : "#ffffff"}
                  strokeWidth={isSel ? 1.6 : 0.5}
                  style={{ cursor: r ? "pointer" : "default", transition: "fill .2s, stroke-width .2s" }}
                  onMouseEnter={(e) => setHover({ name: r ? `${name}: ${r.count} pc · ${r.top_color}` : name, count: r ? r.count : 0, x: e.clientX, y: e.clientY })}
                  onMouseMove={(e) => setHover({ name: r ? `${name}: ${r.count} pc · ${r.top_color}` : name, count: r ? r.count : 0, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => r && setSelected(isSel ? null : name)}
                />
              );
            })}
          </svg>
        )}

        {!drill ? (
          <div className="flex items-center gap-2 mt-3 justify-end text-xs text-muted-page font-tech" data-testid="map-ink-legend">
            <Stamp className="w-3.5 h-3.5" /> Each country inked in its national flag colour · deeper = more pieces
          </div>
        ) : (
          <div className="mt-3 border-t pt-3" style={{ borderColor: "var(--page-border)" }} data-testid="map-color-legend">
            <p className="font-tech text-[11px] uppercase tracking-widest text-muted-page mb-2">Color key · each region shows your most common color there</p>
            {legendColors.length ? (
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {legendColors.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 text-xs" data-testid={`legend-${c.replace(/[^a-zA-Z]/g, "-")}`}>
                    <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ background: colorHex(c) }} />
                    {c}
                  </span>
                ))}
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-page">
                  <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ background: "#ece7dd" }} /> None yet
                </span>
              </div>
            ) : (
              <p className="text-xs text-muted-page">No pieces tagged to a region here yet.</p>
            )}
          </div>
        )}

        {hover && (
          <div className="fixed z-50 pointer-events-none px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-lg" style={{ left: hover.x + 12, top: hover.y + 12, background: "#3a2a10" }} data-testid="map-tooltip">
            {hover.name}{!drill && hover.count > 0 ? `: ${hover.count} piece${hover.count > 1 ? "s" : ""}` : ""}
          </div>
        )}
      </div>

      {drill ? (
        <div>
          <h3 className="font-display text-2xl font-bold mb-3">{drillCfg.label} breakdown</h3>
          {drillRows.length === 0 ? (
            <p className="text-muted-page">No pieces tagged to a {drillCfg.regionWord} yet. Open a piece → “Add to my collection” and pick one.</p>
          ) : (
            <div className="grid gap-2">
              {drillRows.map((r) => {
                const isSel = selected === r.region;
                return (
                  <button
                    key={r.region}
                    data-testid={`region-row-${r.region.replace(/[^a-zA-Z]/g, "-")}`}
                    onClick={() => setSelected(isSel ? null : r.region)}
                    className={`w-full text-left surface border rounded-xl px-4 py-3 flex items-center gap-3 hover:shadow transition-shadow ${isSel ? "border-2" : ""}`}
                    style={isSel ? { borderColor: "var(--honey-500)" } : {}}
                  >
                    <span className="w-4 h-4 rounded-full border border-black/10 shrink-0" style={{ background: colorHex(r.top_color) }} title={r.top_color} />
                    <span className="flex-1 font-medium">{r.region}</span>
                    <span className="text-xs text-muted-page hidden sm:inline">{r.top_color}</span>
                    <span className="font-tech text-sm w-16 text-right brand-text font-bold">{r.count} pc</span>
                  </button>
                );
              })}
            </div>
          )}
          {selectedRegion && (
            <div className="mt-4 surface border rounded-2xl p-4" data-testid="region-pieces">
              <p className="font-tech text-xs uppercase tracking-widest text-muted-page mb-3">Pieces from {selectedRegion.region}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {selectedRegion.pieces.map((p, i) => (
                  <div key={`${p.cd_number}-${i}`} className="flex items-center justify-between border rounded-lg px-3 py-2" style={{ borderColor: "var(--page-border)" }}>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{p.name} <span className="text-muted-page font-normal">· {p.cd_number}</span></p>
                      <p className="text-xs text-muted-page truncate">{p.maker} · {p.color} · Grade {p.grade}</p>
                    </div>
                    <span className="font-tech text-sm shrink-0 ml-2">×{p.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <h3 className="font-display text-2xl font-bold mb-3">Countries breakdown</h3>
          <div className="grid gap-2">
            {rows.map((r) => {
              const isSel = selected && norm(selected) === norm(r.country);
              return (
                <button
                  key={r.country}
                  data-testid={`country-row-${r.country.replace(/[^a-zA-Z]/g, "-")}`}
                  onClick={() => setSelected(isSel ? null : r.country)}
                  className={`w-full text-left surface border rounded-xl px-4 py-3 flex items-center gap-3 hover:shadow transition-shadow ${isSel ? "border-2" : ""}`}
                  style={isSel ? { borderColor: "var(--honey-500)" } : {}}
                >
                  <span className="w-4 h-4 rounded-full border border-black/10 shrink-0" style={{ background: inkColor(norm(r.country)) }} title={`${r.country} flag ink`} />
                  <span className="flex-1 font-medium">{r.country}</span>
                  <div className="w-40 h-3 rounded-full bg-black/5 overflow-hidden hidden sm:block">
                    <div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: inkColor(norm(r.country)) }} />
                  </div>
                  <span className="font-tech text-sm w-16 text-right brand-text font-bold">{r.count} pc</span>
                </button>
              );
            })}
          </div>
          {selectedRow && (
            <div className="mt-4 surface border rounded-2xl p-4" data-testid="country-pieces">
              <p className="font-tech text-xs uppercase tracking-widest text-muted-page mb-3">Pieces from {selectedRow.country}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {selectedRow.pieces.map((p, i) => (
                  <div key={`${p.cd_number}-${i}`} className="flex items-center justify-between border rounded-lg px-3 py-2" style={{ borderColor: "var(--page-border)" }}>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{p.name} <span className="text-muted-page font-normal">· {p.cd_number}</span></p>
                      <p className="text-xs text-muted-page truncate">{p.maker} · {p.color} · Grade {p.grade}</p>
                    </div>
                    <span className="font-tech text-sm shrink-0 ml-2">×{p.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
